import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ConversionStep } from './RouletteConversionChart';

const STEP_NUMBERS = '❶❷❸❹❺❻❼❽❾❿⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴';

function wrapLines(text: string, maxCharsPerLine = 10): string[] {
  const byNewline = text.split('\n');
  const result: string[] = [];
  for (const part of byNewline) {
    if (part.length <= maxCharsPerLine) {
      result.push(part);
    } else {
      for (let i = 0; i < part.length; i += maxCharsPerLine) {
        result.push(part.slice(i, i + maxCharsPerLine));
      }
    }
  }
  return result;
}

const X_AXIS_LABEL_OFFSET = 24;

function XAxisTickNumber(props: {
  x?: number;
  y?: number;
  payload?: { value?: number };
  chartData: { name: string }[];
}) {
  const { x = 0, y = 0, payload, chartData } = props;
  const idx = payload?.value ?? 0;
  const value = chartData[idx]?.name ?? '';
  const lines = wrapLines(value, 10);
  const lineHeight = 14;
  const startY = -(lines.length - 1) * (lineHeight / 2);
  return (
    <g transform={`translate(${x}, ${y + X_AXIS_LABEL_OFFSET})`}>
      <text x={0} y={startY} textAnchor="middle" fill="#000" style={{ fontSize: 15 }}>
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? 0 : lineHeight} textAnchor="middle">
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

const TUBE_LABEL_ORDER = ['도넛', '수박', '오렌지', '키위', '파인애플'];
const TICKET_ORDER = ['1', '2', '3', '4', '5'];

function CustomTooltip({
  active,
  payload,
  formatter,
  selectedContent,
  tubeChoicePercentages,
  moreGameTicketPercentages,
  tubeCompareChoicePercentages,
  moreGameTicketComparePercentages,
}: {
  active?: boolean;
  payload?: Array<{ name?: string | number; value?: unknown; dataKey?: string | number; payload?: { name?: string } }>;
  formatter: (value: number, name: string) => [string, string];
  selectedContent?: string;
  tubeChoicePercentages?: Record<string, number> | null;
  moreGameTicketPercentages?: Record<string, number> | null;
  tubeCompareChoicePercentages?: Record<string, number> | null;
  moreGameTicketComparePercentages?: Record<string, number> | null;
}) {
  if (!active || !payload?.length) return null;
  const first = payload[0]?.payload;
  const headerLabel = String(first?.name ?? '');
  const showTubeBreakdown =
    selectedContent === '튜브 낚시' &&
    headerLabel.includes('튜브 선택') &&
    ((tubeChoicePercentages && Object.keys(tubeChoicePercentages).length > 0) ||
      (tubeCompareChoicePercentages && Object.keys(tubeCompareChoicePercentages).length > 0));
  const showTicketBreakdown =
    selectedContent === '튜브 낚시' &&
    headerLabel.includes('티켓 더 받기') &&
    ((moreGameTicketPercentages && Object.keys(moreGameTicketPercentages).length > 0) ||
      (moreGameTicketComparePercentages && Object.keys(moreGameTicketComparePercentages).length > 0));
  const hasCompareTube = Boolean(tubeCompareChoicePercentages && Object.keys(tubeCompareChoicePercentages).length > 0);
  const hasCompareTicket = Boolean(moreGameTicketComparePercentages && Object.keys(moreGameTicketComparePercentages).length > 0);

  return (
    <Paper
      elevation={8}
      sx={{
        p: 1.5,
        minWidth: 140,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 600 }}>
        {headerLabel}
      </Typography>
      {payload.map((entry) => {
        const value = entry.value;
        if (value == null) return null;
        const name =
          entry.name === 'conversionRate' ? '전환율 (%)' : entry.name === 'count' ? '전환수' : String(entry.name ?? '');
        const [formatted, label] = formatter(Number(value), name);
        return (
          <Typography key={String(entry.dataKey ?? name)} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <span style={{ color: '#666' }}>{label}</span>
            <span style={{ fontWeight: 600 }}>{formatted}</span>
          </Typography>
        );
      })}
      {showTubeBreakdown && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
            튜브별 선택 비율 {hasCompareTube ? '(기준)' : ''}
          </Typography>
          {TUBE_LABEL_ORDER.map((tubeLabel) => {
            const pct = tubeChoicePercentages?.[tubeLabel];
            if (pct == null) return null;
            return (
              <Typography key={tubeLabel} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <span style={{ color: '#666' }}>{tubeLabel}</span>
                <span style={{ fontWeight: 500 }}>{pct}%</span>
              </Typography>
            );
          })}
          {hasCompareTube && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pt: 0.5, fontWeight: 600 }}>
                튜브별 선택 비율 (비교)
              </Typography>
              {TUBE_LABEL_ORDER.map((tubeLabel) => {
                const pct = tubeCompareChoicePercentages?.[tubeLabel];
                if (pct == null) return null;
                return (
                  <Typography key={`compare-${tubeLabel}`} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <span style={{ color: '#666' }}>{tubeLabel}</span>
                    <span style={{ fontWeight: 500 }}>{pct}%</span>
                  </Typography>
                );
              })}
            </>
          )}
        </>
      )}
      {showTicketBreakdown && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', fontWeight: 600 }}>
            티켓별 비율 {hasCompareTicket ? '(기준)' : ''}
          </Typography>
          {TICKET_ORDER.map((ticketKey) => {
            const pct = moreGameTicketPercentages?.[ticketKey];
            if (pct == null) return null;
            return (
              <Typography key={ticketKey} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <span style={{ color: '#666' }}>{ticketKey}티켓</span>
                <span style={{ fontWeight: 500 }}>{pct}%</span>
              </Typography>
            );
          })}
          {hasCompareTicket && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, pt: 0.5, fontWeight: 600 }}>
                티켓별 비율 (비교)
              </Typography>
              {TICKET_ORDER.map((ticketKey) => {
                const pct = moreGameTicketComparePercentages?.[ticketKey];
                if (pct == null) return null;
                return (
                  <Typography key={`compare-${ticketKey}`} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <span style={{ color: '#666' }}>{ticketKey}티켓</span>
                    <span style={{ fontWeight: 500 }}>{pct}%</span>
                  </Typography>
                );
              })}
            </>
          )}
        </>
      )}
    </Paper>
  );
}

/** 기간 비교용 색상 */
const COLOR_COMPARE_BAR = '#FED7AA';
const COLOR_COMPARE_LINE = '#9A3412';
const COLOR_COMPARE_LINE_DOT = '#7C2D12';

type Props = {
  conversionData: ConversionStep[];
  compareConversionData?: ConversionStep[];
  selectedContent?: string;
  tubeChoicePercentages?: Record<string, number> | null;
  moreGameTicketPercentages?: Record<string, number> | null;
  tubeCompareChoicePercentages?: Record<string, number> | null;
  moreGameTicketComparePercentages?: Record<string, number> | null;
};

export default function RouletteChartGraph({ conversionData, compareConversionData, selectedContent, tubeChoicePercentages, moreGameTicketPercentages, tubeCompareChoicePercentages, moreGameTicketComparePercentages }: Props) {
  const hasCompare = Boolean(compareConversionData?.length && compareConversionData.length === conversionData.length);

  const chartData = useMemo(() => {
    return conversionData.map((step, index) => {
      const compareStep = hasCompare ? compareConversionData?.[index] : undefined;
      return {
        x: index,
        name: (STEP_NUMBERS[index] ?? `${index + 1}`) + ' ' + step.label,
        label: step.label,
        conversionRate: step.percentage,
        count: step.count,
        compareConversionRate: compareStep?.percentage,
        compareCount: compareStep?.count,
      };
    });
  }, [conversionData, compareConversionData, hasCompare]);

  const lineData = useMemo(() => {
    if (chartData.length === 0) return [];
    const first = chartData[0];
    const last = chartData[chartData.length - 1];
    if (!first || !last) return [];
    const n = chartData.length;
    return [
      { ...first, x: -0.5, count: 0, compareCount: 0 },
      ...chartData,
      { ...last, x: n - 0.5, count: 0, compareCount: 0 },
    ];
  }, [chartData]);

  const n = chartData.length;

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        p: 2,
        mt: 3,
      }}
    >
      <Box sx={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={lineData}
            margin={{ top: 5, right: 30, left: 20, bottom: 56 }}
            barCategoryGap="40%"
            barGap={0}
          >
            <XAxis
              type="number"
              dataKey="x"
              domain={n > 0 ? [-0.5, n - 0.5] : [0, 0]}
              ticks={chartData.map((d) => d.x)}
              interval={0}
              tick={<XAxisTickNumber chartData={chartData} />}
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: '#000', fontSize: 12 }}
              tickFormatter={(value: number) => `${value}%`}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#000', fontSize: 12 }}
              tickFormatter={(value: number) => Number(value).toLocaleString('ko-KR')}
            />
            <RechartsTooltip
              content={(props) => (
                <CustomTooltip
                  {...props}
                  selectedContent={selectedContent}
                  tubeChoicePercentages={tubeChoicePercentages}
                  moreGameTicketPercentages={moreGameTicketPercentages}
                  tubeCompareChoicePercentages={tubeCompareChoicePercentages}
                  moreGameTicketComparePercentages={moreGameTicketComparePercentages}
                  formatter={(value: number, name: string) => {
                    if (name === 'conversionRate' || name === '기준 전환율 (%)') {
                      return [`${Number(value).toFixed(2)}%`, '기준 전환율 (%)'];
                    }
                    if (name === 'compareConversionRate' || name === '비교 전환율 (%)') {
                      return [`${Number(value).toFixed(2)}%`, '비교 전환율 (%)'];
                    }
                    if (name === 'count' || name === '기준 전환수') {
                      return [Number(value).toLocaleString('ko-KR'), '기준 전환수'];
                    }
                    if (name === 'compareCount' || name === '비교 전환수') {
                      return [Number(value).toLocaleString('ko-KR'), '비교 전환수'];
                    }
                    return [typeof value === 'number' ? Number(value).toLocaleString('ko-KR') : String(value), name];
                  }}
                />
              )}
            />
            <Bar
              yAxisId="right"
              dataKey="count"
              fill="#E5E7EB"
              radius={[4, 4, 0, 0]}
              barSize={hasCompare ? 32 : 64}
              name="기준 전환수"
            />
            {hasCompare && (
              <Bar
                yAxisId="right"
                dataKey="compareCount"
                fill={COLOR_COMPARE_BAR}
                radius={[4, 4, 0, 0]}
                barSize={32}
                name="비교 전환수"
              />
            )}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="conversionRate"
              stroke="#F97316"
              strokeWidth={2}
              dot={true}
              activeDot={{ fill: '#F97316', strokeWidth: 2, r: 4 }}
              name="기준 전환율 (%)"
            />
            {hasCompare && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="compareConversionRate"
                stroke={COLOR_COMPARE_LINE}
                strokeWidth={2}
                dot={{ fill: COLOR_COMPARE_LINE_DOT, r: 3 }}
                activeDot={{ fill: COLOR_COMPARE_LINE_DOT, strokeWidth: 2, r: 4 }}
                name="비교 전환율 (%)"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#E5E7EB' }} />
          <Box component="span" sx={{ fontSize: 14, color: '#666' }}>기준 전환수</Box>
        </Box>
        {hasCompare && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: COLOR_COMPARE_BAR }} />
            <Box component="span" sx={{ fontSize: 14, color: '#666' }}>비교 전환수</Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 24, height: 3, borderRadius: 1, bgcolor: '#F97316', position: 'relative', '&::before': { content: '""', position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 8, height: 8, borderRadius: '50%', bgcolor: '#F97316', border: '2px solid #F97316', boxSizing: 'border-box' } }} />
          <Box component="span" sx={{ fontSize: 14, color: '#666' }}>기준 전환율 (%)</Box>
        </Box>
        {hasCompare && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 24, height: 3, borderRadius: 1, bgcolor: COLOR_COMPARE_LINE }} />
            <Box component="span" sx={{ fontSize: 14, color: '#666' }}>비교 전환율 (%)</Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
